import { inject, Injectable, NgZone, DOCUMENT } from "@angular/core";
import { BehaviorSubject, distinctUntilChanged, filter, fromEvent, map, merge, Observable, shareReplay, startWith, Subject, switchMap, timer } from "rxjs";
import { AuthService } from "./auth.service";
import { environment } from "@environments/environment";
import { Dialog, DialogRef } from "@angular/cdk/dialog";
import { SessionTimeoutDialogComponent } from "@shared/components/session-timeout-dialog/session-timeout-dialog.component";

import { SessionTimeoutEvent, SessionTimeoutConfig, SessionTimeoutState } from "@shared/models/session-timeout.model";
import { ToastNotificationService } from "@shared/services/toast-notification.service";

@Injectable({
  providedIn: "root",
})
export class SessionTimeoutService {
  private readonly authService = inject(AuthService);
  private readonly dialog = inject(Dialog);
  private readonly ngZone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly toastService = inject(ToastNotificationService);

  private readonly config: SessionTimeoutConfig = {
    timeoutDurationMs: environment.sessionTimeout.timeoutMinutes * 60 * 1000,
    warningDurationMs: environment.sessionTimeout.warningMinutes * 60 * 1000,
    resetEvents: ["mousedown", "mousemove", "keypress", "scroll", "touchstart", "click"],
    showWarning: environment.sessionTimeout.showWarning,
  };

  private readonly STORAGE_KEY = "session_timeout_state";
  private readonly LAST_ACTIVITY_KEY = "session_last_activity";

  private readonly sessionState$ = new BehaviorSubject<SessionTimeoutState>({
    timeRemaining: this.config.timeoutDurationMs,
    isActive: false,
    showWarning: false,
    isExpired: false,
  });

  private readonly sessionEvents$ = new Subject<SessionTimeoutEvent>();
  private readonly userActivity$ = new Subject<void>();
  private readonly timerReset$ = new Subject<void>();

  private isInitialized = false;
  private dialogRef: DialogRef<unknown, SessionTimeoutDialogComponent> | null = null;

  readonly state$ = this.sessionState$.pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    shareReplay(1),
  );

  readonly showWarning$ = this.state$.pipe(
    map((state) => state.showWarning),
    distinctUntilChanged(),
  );

  initialize(): void {
    if (this.isInitialized) return;

    this.setupUserActivityTracking();
    this.setupSessionTimer();
    this.setupAuthStateTracking();
    this.setupWarningModal();
    this.setupCrossTabSync();

    this.isInitialized = true;
  }

  private setupWarningModal(): void {
    this.showWarning$.subscribe((isShow) => {
      if (isShow) {
        this.openWarningModal();
      } else {
        this.closeWarningModal();
      }
    });
  }

  private setupUserActivityTracking(): void {
    const domEvents$ = this.createDomEventStream();
    const allActivity$ = merge(domEvents$, this.userActivity$).pipe(startWith(null));

    allActivity$.subscribe(() => {
      if (this.sessionState$.value.isActive) {
        this.handleUserActivity();
      }
    });
  }

  private createDomEventStream(): Observable<Event> {
    return this.ngZone.runOutsideAngular(() => merge(...this.config.resetEvents.map((event) => fromEvent(this.document, event, { passive: true }))));
  }

  private setupSessionTimer(): void {
    const timerTrigger$ = this.createTimerTriggerStream();
    const sessionTimer$ = timerTrigger$.pipe(switchMap(() => this.createCountdownTimer()));

    sessionTimer$.subscribe((timeRemaining) => this.processTimerTick(timeRemaining));
  }

  private createTimerTriggerStream(): Observable<true | void> {
    const sessionActive$ = this.sessionState$.pipe(
      map((state) => state.isActive),
      distinctUntilChanged(),
      filter(Boolean),
    );

    const timerReset$ = this.timerReset$.pipe(filter(() => this.sessionState$.value.isActive));

    return merge(sessionActive$, timerReset$);
  }

  private createCountdownTimer(): Observable<number> {
    return timer(0, 1000).pipe(map((tickCount) => Math.max(0, this.config.timeoutDurationMs - tickCount * 1000)));
  }

  private setupAuthStateTracking(): void {
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.handleAuthStateChange(isAuthenticated);
    });
  }

  private handleAuthStateChange(isAuthenticated: boolean): void {
    if (isAuthenticated) {
      this.startSession();
    } else {
      this.stopSession();
    }
  }

  private startSession(): void {
    if (!this.authService.isAuthenticated()) return;

    const timestamp = Date.now();

    this.updateSessionState({
      isActive: true,
      timeRemaining: this.config.timeoutDurationMs,
      showWarning: false,
      isExpired: false,
      lastActivity: timestamp,
    });

    this.saveLastActivityToStorage();

    this.sessionEvents$.next(SessionTimeoutEvent.RESET);
  }

  private stopSession(): void {
    this.updateSessionState({
      isActive: false,
      showWarning: false,
      isExpired: false,
    });

    this.clearSessionStorageData();
  }

  private handleUserActivity(): void {
    const timestamp = Date.now();

    this.updateSessionState({
      timeRemaining: this.config.timeoutDurationMs,
      showWarning: false,
      lastActivity: timestamp,
    });

    this.saveLastActivityToStorage();

    this.timerReset$.next();
    this.sessionEvents$.next(SessionTimeoutEvent.RESET);
  }

  private processTimerTick(timeRemaining: number): void {
    const currentState = this.sessionState$.value;

    if (!currentState.isActive) {
      return;
    }

    const shouldShowWarning = this.shouldShowWarning(timeRemaining);
    const isExpired = timeRemaining <= 0;

    this.updateSessionState({
      timeRemaining,
      showWarning: shouldShowWarning,
      isExpired,
    });

    this.handleWarningAndExpiration(currentState, shouldShowWarning, isExpired);
  }

  private shouldShowWarning(timeRemaining: number): boolean {
    return this.config.showWarning && timeRemaining <= this.config.warningDurationMs && timeRemaining > 0;
  }

  private handleWarningAndExpiration(currentState: SessionTimeoutState, shouldShowWarning: boolean, isExpired: boolean): void {
    if (shouldShowWarning && !currentState.showWarning) {
      this.sessionEvents$.next(SessionTimeoutEvent.WARNING);
    }

    if (isExpired && !currentState.isExpired) {
      this.handleSessionExpired();
    }
  }

  private handleSessionExpired(): void {
    this.sessionEvents$.next(SessionTimeoutEvent.EXPIRED);
    this.updateSessionState({
      isActive: false,
      showWarning: false,
      isExpired: true,
    });

    this.clearSessionStorageData();

    this.authService.logout();
    this.toastService.warning("session_timeout.notification.auto_logout");
  }

  private updateSessionState(updates: Partial<SessionTimeoutState>): void {
    const currentState = this.sessionState$.value;
    this.sessionState$.next({ ...currentState, ...updates });
  }

  private openWarningModal(): void {
    this.dialogRef = this.dialog.open(SessionTimeoutDialogComponent);
  }

  private closeWarningModal(): void {
    this.dialogRef?.close();
    this.dialogRef = null;
  }

  private setupCrossTabSync(): void {
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener("storage", (event) => {
        if (event.key === this.LAST_ACTIVITY_KEY && event.newValue) {
          this.handleCrossTabActivity(parseInt(event.newValue, 10));
        }
      });
    });

    this.loadSessionStateFromStorage();
  }

  private handleCrossTabActivity(activityTimestamp: number): void {
    const currentState = this.sessionState$.value;

    if (!currentState.isActive) {
      return;
    }

    const timeSinceActivity = Date.now() - activityTimestamp;
    const newTimeRemaining = Math.max(0, this.config.timeoutDurationMs - timeSinceActivity);

    const currentTimeSinceActivity = this.config.timeoutDurationMs - currentState.timeRemaining;

    if (timeSinceActivity < currentTimeSinceActivity) {
      this.updateSessionState({
        timeRemaining: newTimeRemaining,
        showWarning: this.shouldShowWarning(newTimeRemaining),
        lastActivity: activityTimestamp,
      });

      this.timerReset$.next();
    }
  }

  private saveLastActivityToStorage(): void {
    const timestamp = Date.now();
    localStorage.setItem(this.LAST_ACTIVITY_KEY, timestamp.toString());
  }

  private loadSessionStateFromStorage(): void {
    const lastActivityStr = localStorage.getItem(this.LAST_ACTIVITY_KEY);

    if (lastActivityStr && this.authService.isAuthenticated()) {
      const lastActivity = parseInt(lastActivityStr, 10);
      const timeSinceActivity = Date.now() - lastActivity;
      const timeRemaining = Math.max(0, this.config.timeoutDurationMs - timeSinceActivity);

      if (timeRemaining > 0) {
        this.updateSessionState({
          timeRemaining,
          isActive: true,
          showWarning: this.shouldShowWarning(timeRemaining),
          isExpired: false,
          lastActivity,
        });
      } else {
        this.handleSessionExpired();
      }
    }
  }

  private clearSessionStorageData(): void {
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
    localStorage.removeItem(this.STORAGE_KEY);
  }
}
